import { z } from "zod/v4";

import {
  AgenticLogicBranchFiredDescriptions,
  AgenticLogicBundleChildDescriptions,
  AgenticLogicItemDiscountDescriptions,
  AgenticLogicResultEnvelopeDescriptions,
  AgenticLogicResultItemDescriptions,
} from "../../../descriptions/agentic-logic.describe";
import { assertDescriptions } from "../../../descriptions/types";
// One vocabulary for the presentation half — the same list the upsell DTOs and
// the checkout-blocks controller speak, so there is no parity assertion to rot.
import {
  AGENTIC_LOGIC_ENVELOPE_VERSION,
  AgenticLogicItemDiscountTypeSchema,
  AgenticLogicItemIntentSchema,
  AgenticLogicItemKindSchema,
} from "./vocab";

// ---------------------------------------------------------------------------
// Result side
// ---------------------------------------------------------------------------

/** RESERVED shape — V1 activation is post-purchase only. */
export const AgenticLogicItemDiscountSchema = z
  .object({
    type: AgenticLogicItemDiscountTypeSchema.describe(
      AgenticLogicItemDiscountDescriptions.type
    ),
    value: z
      .number()
      .nonnegative()
      .describe(AgenticLogicItemDiscountDescriptions.value),
  })
  .describe(AgenticLogicItemDiscountDescriptions._schema);

export type AgenticLogicItemDiscount = z.infer<
  typeof AgenticLogicItemDiscountSchema
>;

export const AgenticLogicBundleChildSchema = z
  .object({
    kind: AgenticLogicItemKindSchema.describe(
      AgenticLogicBundleChildDescriptions.kind
    ),
    id: z.string().describe(AgenticLogicBundleChildDescriptions.id),
    quantity: z
      .number()
      .int()
      .positive()
      .describe(AgenticLogicBundleChildDescriptions.quantity),
  })
  .describe(AgenticLogicBundleChildDescriptions._schema);

export type AgenticLogicBundleChild = z.infer<
  typeof AgenticLogicBundleChildSchema
>;

export const AgenticLogicResultItemSchema = z
  .object({
    kind: AgenticLogicItemKindSchema.describe(
      AgenticLogicResultItemDescriptions.kind
    ),
    id: z.string().describe(AgenticLogicResultItemDescriptions.id),
    defaultVariantId: z
      .string()
      .optional()
      .describe(AgenticLogicResultItemDescriptions.defaultVariantId),
    // The merchant's permitted-variant set. ABSENT means unrestricted; a
    // present list is exhaustive. Surfaces that mint per-variant discount
    // signatures MUST gate on this — `signIncludedUpsellVariant` refuses to
    // sign an excluded variant precisely so a signature cannot be minted for
    // something the shopper may not add, and without this field a hydrator
    // would have to assume "all included" and mint those signatures anyway.
    allowedVariantIds: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicResultItemDescriptions.allowedVariantIds),
    sellingPlanId: z
      .string()
      .optional()
      .describe(AgenticLogicResultItemDescriptions.sellingPlanId),
    intent: AgenticLogicItemIntentSchema.optional().describe(
      AgenticLogicResultItemDescriptions.intent
    ),
    discount: AgenticLogicItemDiscountSchema.optional().describe(
      AgenticLogicResultItemDescriptions.discount
    ),
    sourceBranch: z
      .string()
      .optional()
      .describe(AgenticLogicResultItemDescriptions.sourceBranch),
    children: z
      .array(AgenticLogicBundleChildSchema)
      .optional()
      .describe(AgenticLogicResultItemDescriptions.children),
  })
  .describe(AgenticLogicResultItemDescriptions._schema);

export type AgenticLogicResultItem = z.infer<
  typeof AgenticLogicResultItemSchema
>;

export const AgenticLogicBranchFiredSchema = z
  .object({
    base: z.string().describe(AgenticLogicBranchFiredDescriptions.base),
    instance: z
      .string()
      .optional()
      .describe(AgenticLogicBranchFiredDescriptions.instance),
  })
  .describe(AgenticLogicBranchFiredDescriptions._schema);

export type AgenticLogicBranchFired = z.infer<
  typeof AgenticLogicBranchFiredSchema
>;

export const AgenticLogicResultEnvelopeSchema = z
  .object({
    version: z
      .literal(AGENTIC_LOGIC_ENVELOPE_VERSION)
      .describe(AgenticLogicResultEnvelopeDescriptions.version),
    items: z
      .array(AgenticLogicResultItemSchema)
      .describe(AgenticLogicResultEnvelopeDescriptions.items),
    branchFired: AgenticLogicBranchFiredSchema.optional().describe(
      AgenticLogicResultEnvelopeDescriptions.branchFired
    ),
    cartFingerprint: z
      .string()
      .optional()
      .describe(AgenticLogicResultEnvelopeDescriptions.cartFingerprint),
    warnings: z
      .array(z.string())
      .optional()
      .describe(AgenticLogicResultEnvelopeDescriptions.warnings),
    // Free-form and read by NOTHING. No key here is interpreted: a rule
    // decides WHICH products are recommended, never what they cost. Pricing
    // and price presentation are the PLACEMENT's, set by the merchant on the
    // block or component the rule is attached to.
    metadata: z
      .record(z.string(), z.unknown())
      .optional()
      .describe(AgenticLogicResultEnvelopeDescriptions.metadata),
  })
  .describe(AgenticLogicResultEnvelopeDescriptions._schema);

export type AgenticLogicResultEnvelope = z.infer<
  typeof AgenticLogicResultEnvelopeSchema
>;

assertDescriptions<
  typeof AgenticLogicItemDiscountDescriptions,
  AgenticLogicItemDiscount
>();
assertDescriptions<
  typeof AgenticLogicBundleChildDescriptions,
  AgenticLogicBundleChild
>();
assertDescriptions<
  typeof AgenticLogicResultItemDescriptions,
  AgenticLogicResultItem
>();
assertDescriptions<
  typeof AgenticLogicBranchFiredDescriptions,
  AgenticLogicBranchFired
>();
assertDescriptions<
  typeof AgenticLogicResultEnvelopeDescriptions,
  AgenticLogicResultEnvelope
>();
